<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue'

import {
  Confirmation,
  IncidentActionType,
} from '@collector/sportsapi-client-legacy'
import Pagination from '@mobile/components/Pagination/Pagination.vue'
import { useSportOptions } from '@mobile/views/Relation/DetailsPanel/Sports/composables/useSportOptions'
import { useEventRelationSettings } from '@mobile/views/Relation/DetailsPanel/Sports/useEventRelationSettings'
import Table from '@mobile/views/Relation/DetailsPanel/Table/Table.vue'
import { ProbableLinkedIncident } from '@mobile/views/Relation/Shared/RelationDependencies/types'
import { useEventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'
import { useSportConfiguration } from '@mobile/views/Relation/Shared/RelationSportCommonDependencies/useSportConfiguration'

import { useIncidentResult } from '../../Shared/IncidentResult/composables/useIncidentResult'
import { getEmptyIncidents } from './emptyIncidentsFilter'
import {
  getHistoryPageForDockedIncident,
  getHistoryPageForVIPDock,
  injectFilteredOutDockedIncidentIntoPage,
  isDockedIncidentInFilteredList,
} from './historyIncidentsDock.utils'
import HistoryItem from './HistoryItem.vue'
import HistoryItemTableRow from './HistoryItemTableRow.vue'
import { useDockedIncidentComponent } from './useDockedIncidentComponent'
import { useHistoryIncidents } from './useHistoryIncidents'
import {
  filterEditableIncidents,
  filterIncidentsByKeyPassAttribute,
  filterIncidentsWithSameEventTime,
  filterNotEmergencyIncidents,
  sortProbableLinkedIncidentsByEventTimeAndStatus,
  sortProbableLinkedIncidentsByOccuredAtField,
} from './utils'

type Props = {
  reversePagination?: boolean
}

const props = defineProps<Props>()

const eventInjections = useEventInjections()
const sportOptionsComposable = useSportOptions(eventInjections.sport)
const { linkedProbableIncidents, sport } = eventInjections
const {
  assignableIncidentsConfiguration,
  disabledIncidentsWithSameTime,
  enabledIncidentSequencesWithSameTime,
  eventClockConfiguration,
} = useSportConfiguration()
const {
  participantsIncidents,
  hideIncidents,
  incidentType,
  detailsIncidents,
  enableFilters,
  emptyIncidents,
  eventParticipants,
  filterKeyPasses,
  showIncidentsWithSameEventTimeFilterEnabled,
} = useEventRelationSettings(eventInjections, sportOptionsComposable)
const { detailsConfiguration, secondParticipantIncidents } =
  useSportOptions(sport)
const { incidentsPerPage } = useEventRelationSettings(
  eventInjections,
  sportOptionsComposable,
)
const { getResult } = useIncidentResult()
const { historyPageResetNonce, historyPageDockIncidentId } =
  useHistoryIncidents(eventInjections)
const {
  dockedLinkedIncident,
  position,
  shouldFocusOnMinutesInput,
  openedMainSelect,
  openedSecondarySelect,
  openedTernarySelect,
} = useDockedIncidentComponent(eventInjections)
const currentPage = ref(1)

watch(
  historyPageResetNonce,
  () => {
    const dockedIncidentId = historyPageDockIncidentId.value
    if (!dockedIncidentId) {
      return
    }

    const page =
      getHistoryPageForDockedIncident(
        !!props.reversePagination,
        filteredIncidents.value,
        incidentsPerPage.value,
        dockedIncidentId,
      ) ??
      getHistoryPageForVIPDock(
        !!props.reversePagination,
        filteredIncidents.value.length,
        incidentsPerPage.value,
      )

    currentPage.value = page
  },
  { flush: 'sync' },
)

function filterByParticipantWhenFilterIsEnabled(
  incidents: ProbableLinkedIncident[],
): ProbableLinkedIncident[] {
  return eventParticipants.value
    ? incidents.filter(
        (incident) => incident.participant?.id === eventParticipants.value,
      )
    : incidents
}

function containsIncidentId(
  probableLinkedIncident: ProbableLinkedIncident,
): (incidentId: number) => boolean {
  return (incidentId) =>
    incidentId === probableLinkedIncident.incident.incident_id
}

const filteredIncidents = computed(() => {
  let incidents = linkedProbableIncidents.value

  incidents = filterByParticipantWhenFilterIsEnabled(incidents)

  if (showIncidentsWithSameEventTimeFilterEnabled.value) {
    sortProbableLinkedIncidentsByOccuredAtField(incidents)
    incidents = filterEditableIncidents(incidents)
    incidents = filterNotEmergencyIncidents(incidents)
    incidents = filterIncidentsWithSameEventTime({
      incidents,
      disabledIncidentsWithSameTime,
      enabledIncidentSequencesWithSameTime,
    })
  }

  if (filterKeyPasses.value) {
    if (incidentType.value.length) {
      return incidents
        .filter((incident) =>
          incidentType.value.includes(incident.incident.incident_id),
        )
        .concat(
          filterByParticipantWhenFilterIsEnabled(
            filterIncidentsByKeyPassAttribute(linkedProbableIncidents.value),
          ),
        )
    } else {
      incidents = filterIncidentsByKeyPassAttribute(incidents)
    }
  }

  if (incidentType.value.length) {
    return incidents.filter((incident) =>
      incidentType.value.includes(incident.incident.incident_id),
    )
  }

  if (emptyIncidents.value) {
    const participantsAndDetailsIncidents = incidents.filter(
      (incident) =>
        (participantsIncidents.value.some(containsIncidentId(incident)) ||
          detailsIncidents.value.some(containsIncidentId(incident))) &&
        incident.incident.action !== IncidentActionType.DELETE &&
        incident.incident.confirmation !== Confirmation.Cancelled,
    )

    return getEmptyIncidents(
      participantsAndDetailsIncidents,
      secondParticipantIncidents,
      detailsConfiguration,
      assignableIncidentsConfiguration,
    )
  }

  if (hideIncidents.value.length && enableFilters.value) {
    incidents = incidents.filter((incident) => {
      return hideIncidents.value.every((hideIncident) => {
        return hideIncident !== incident.incident.incident_id
      })
    })
  }

  if (
    !showIncidentsWithSameEventTimeFilterEnabled.value &&
    !props.reversePagination
  ) {
    sortProbableLinkedIncidentsByEventTimeAndStatus(incidents)
  }

  return incidents
})

function getReversePaginatedIncidents(): ProbableLinkedIncident[] {
  const totalItems = filteredIncidents.value.length
  const remainingItems = totalItems % incidentsPerPage.value
  const firstPageSize =
    remainingItems || (totalItems > 0 ? incidentsPerPage.value : 0)

  if (currentPage.value === 1) {
    return filteredIncidents.value.slice(0, firstPageSize)
  } else {
    const startIndex =
      firstPageSize + (currentPage.value - 2) * incidentsPerPage.value
    const endIndex = startIndex + incidentsPerPage.value
    return filteredIncidents.value.slice(startIndex, endIndex)
  }
}

function getPaginatedIncidents(): ProbableLinkedIncident[] {
  const startIndex = (currentPage.value - 1) * incidentsPerPage.value
  const endIndex = startIndex + incidentsPerPage.value
  return filteredIncidents.value.slice(startIndex, endIndex)
}

const filteredPaginatedIncidents = computed(() => {
  if (props.reversePagination) {
    return getReversePaginatedIncidents()
  } else {
    return getPaginatedIncidents()
  }
})

const dockedIncident = computed((): ProbableLinkedIncident | null => {
  return dockedLinkedIncident.value as ProbableLinkedIncident | null
})

const incidentsForRender = computed(() => {
  const pageIncidents = filteredPaginatedIncidents.value
  const docked = dockedIncident.value

  if (
    !docked ||
    isDockedIncidentInFilteredList(filteredIncidents.value, docked.incident.id)
  ) {
    return pageIncidents
  }

  return injectFilteredOutDockedIncidentIntoPage(
    pageIncidents,
    docked,
    !!props.reversePagination,
  )
})

async function handlePageCreated(): Promise<void> {
  const page = currentPage.value
  if (props.reversePagination) {
    await nextTick()
    currentPage.value = page + 1
  }
}

watch(
  () => props.reversePagination,
  (isReverse) => {
    if (isReverse && filteredIncidents.value.length > 0) {
      currentPage.value = Math.ceil(
        filteredIncidents.value.length / incidentsPerPage.value,
      )
    }
  },
  { immediate: true },
)
</script>

<template>
  <div>
    <Table
      class="history-table"
      :showEventTime="eventClockConfiguration.isEnabled"
    >
      <HistoryItem
        v-for="linkedIncident of incidentsForRender"
        :key="`paginated-incident-${linkedIncident.incident.id}`"
        :linkedIncident
        :result="getResult(linkedIncident.incident)"
      />
      <HistoryItemTableRow
        v-if="dockedIncident && position"
        :linkedIncident="dockedIncident"
        :result="getResult(dockedIncident.incident)"
        :position
        :docked="true"
        :shouldFocusOnMinutesInput
        :openedMainSelect
        :openedSecondarySelect
        :openedTernarySelect
      />
    </Table>
    <Pagination
      v-model:currentPage="currentPage"
      class="items-center justify-center py-6"
      :perPage="incidentsPerPage"
      :total="filteredIncidents.length"
      :currentPageTotal="filteredPaginatedIncidents.length"
      @pageCreated="handlePageCreated"
    />
  </div>
</template>
